import { useState, useCallback } from 'react';
import { useAgent } from '../../../hooks/useAgent';
import { gamificationStore } from '../../../utils/gamificationStore';
import { lessonGamificationService } from '../services/lesson-gamification.service';
import { Interaction, InteractionState } from '../types/lesson.types';

interface UseLessonAgentProps {
  interactions: Interaction[];
  currentInteractionIndex: number;
  interactionStates: Record<string, InteractionState>;
  interactionStatesRef: React.MutableRefObject<Record<string, InteractionState>>;
  setInteractionStates: React.Dispatch<React.SetStateAction<Record<string, InteractionState>>>;
  lessonData: any;
  fullTopic: string;
  setIsResuming: (val: boolean) => void;
  t: any;
  gender: number | undefined;
}

export const useLessonAgent = ({
  interactions,
  currentInteractionIndex,
  interactionStates,
  interactionStatesRef,
  setInteractionStates,
  lessonData,
  fullTopic,
  setIsResuming,
  t,
  gender
}: UseLessonAgentProps) => {
  const agent = useAgent();
  const [activeAgentItemId, setActiveAgentItemId] = useState<string | null>(null);
  const [chatThreads, setChatThreads] = useState<Record<string, { role: 'user' | 'assistant', content: string, isError?: boolean }[]>>({});

  const buildAgentMetadata = useCallback((item: any, state: typeof interactionStates[string]) => {
    const base = { topic: fullTopic };
    if (item.type === 'quiz') {
      return {
        ...base,
        question: item.quiz.question,
        options: JSON.stringify(item.quiz.options),
        correct_option: item.quiz.correct_option,
        user_selected_option: state?.selectedOption || ''
      };
    }
    return { ...base, context: item.content || item.title || '' };
  }, [fullTopic]);

  const handleWantExplanation = async (quizItem?: any) => {
    const currentItem = quizItem || interactions[currentInteractionIndex];
    if (!currentItem) return;

    const stepType = currentItem.type === 'content' ? 'teacher_content' : currentItem.type === 'summary' ? 'teacher_summary' : currentItem.type;
    const metadata = buildAgentMetadata(currentItem, interactionStatesRef.current[currentItem.id]);

    try {
      setActiveAgentItemId(currentItem.id);

      setInteractionStates(prev => ({
        ...prev,
        [currentItem.id]: { 
          ...(prev[currentItem.id] || {}), 
          isExplanationAsked: true, 
          isChatDone: false
        }
      }));

      const response = await agent.getExplanation(
        stepType,
        metadata,
        chatThreads[currentItem.id],
        lessonData?.id,
        lessonData?.level_id
      );

      if (response) {
        setChatThreads(prev => {
          const thread = prev[currentItem.id] || [];
          return { ...prev, [currentItem.id]: [...thread, { role: 'assistant', content: response.message, isError: response.isError }] };
        });
      }
    } catch (error) {
      console.error('Failed to get agent explanation', error);
    } finally {
      setActiveAgentItemId(null);
    }
  };

  const handleWantClue = async (quizItem?: any) => {
    setIsResuming(false);
    const currentItem = quizItem || interactions[currentInteractionIndex];
    if (!currentItem || currentItem.type !== 'quiz') return;

    const metadata = buildAgentMetadata(currentItem, interactionStatesRef.current[currentItem.id]);

    try {
      setActiveAgentItemId(currentItem.id);
      
      const optimisticPenalty = lessonGamificationService.penalizeHintUsed(
        lessonData?.id,
        currentItem.stepNumber,
        currentItem.phaseIndex,
        (correction) => {
          setInteractionStates(prev => ({
            ...prev,
            [currentItem.id]: { 
              ...prev[currentItem.id], 
              pointsEarned: (prev[currentItem.id]?.pointsEarned || 0) + correction 
            }
          }));
        }
      );

      setInteractionStates(prev => ({
        ...prev,
        [currentItem.id]: { 
          ...(prev[currentItem.id] || {}), 
          isClueAsked: true, 
          isChatDone: false,
          pointsEarned: (prev[currentItem.id]?.pointsEarned || 0) + optimisticPenalty
        }
      }));

      const response = await agent.getClue(
        'quiz',
        metadata,
        chatThreads[currentItem.id],
        lessonData?.id,
        lessonData?.level_id
      );

      if (response) {
        setChatThreads(prev => {
          const thread = prev[currentItem.id] || [];
          return { ...prev, [currentItem.id]: [...thread, { role: 'assistant', content: response.message, isError: response.isError }] };
        });
      }
    } catch (error) {
      console.error('Failed to get agent clue', error);
    } finally {
      setActiveAgentItemId(null);
    }
  };

  const handleSendFreeText = async (userMessage: string) => {
    setIsResuming(false);
    const currentItem = interactions[currentInteractionIndex];
    if (!currentItem) return;

    const stepType = currentItem.type === 'content' ? 'teacher_content' : currentItem.type === 'summary' ? 'teacher_summary' : currentItem.type;
    const metadata = buildAgentMetadata(currentItem, interactionStates[currentItem.id]);
    const currentThread = chatThreads[currentItem.id] || [];

    try {
      // Append user message and mark chat as "in progress" immediately
      setChatThreads(prev => {
        const thread = prev[currentItem.id] || [];
        return { ...prev, [currentItem.id]: [...thread, { role: 'user', content: userMessage }] };
      });
      setInteractionStates(prev => ({
        ...prev,
        [currentItem.id]: { ...prev[currentItem.id], isChatDone: false }
      }));
      setActiveAgentItemId(currentItem.id);

      const response = await agent.sendMessage(
        userMessage,
        stepType,
        metadata,
        currentThread,
        lessonData?.id,
        lessonData?.level_id
      );

      if (response) {
        setChatThreads(prev => {
          const thread = prev[currentItem.id] || [];
          return { ...prev, [currentItem.id]: [...thread, { role: 'assistant', content: response.message, isError: response.isError }] };
        });

        if (!response.isError) {
          const isClueIntent = response.intent === 'request_clue';
          const isExplanationIntent = ['request_explanation', 'math_help'].includes(response.intent);

          if (isClueIntent) {
            const optimisticPenalty = lessonGamificationService.penalizeHintUsed(
              lessonData?.id,
              currentItem.stepNumber,
              currentItem.phaseIndex,
              (correction) => {
                setInteractionStates(prev => ({
                  ...prev,
                  [currentItem.id]: { 
                    ...prev[currentItem.id], 
                    pointsEarned: (prev[currentItem.id]?.pointsEarned || 0) + correction 
                  }
                }));
              }
            );
            
            setInteractionStates(prev => ({
              ...prev,
              [currentItem.id]: {
                ...prev[currentItem.id],
                isExplanationAsked: false,
                isClueAsked: true,
                pointsEarned: (prev[currentItem.id]?.pointsEarned || 0) + optimisticPenalty
              }
            }));
          } else if (isExplanationIntent) {
            setInteractionStates(prev => ({
              ...prev,
              [currentItem.id]: {
                ...prev[currentItem.id],
                isExplanationAsked: true
              }
            }));
          }
        }
      }
    } catch (error) {
      console.error('Failed to get agent response for free text', error);
    } finally {
      setActiveAgentItemId(null);
    }
  };

  return {
    agent,
    activeAgentItemId,
    chatThreads,
    setChatThreads,
    handleWantExplanation,
    handleWantClue,
    handleSendFreeText
  };
};
// Agent synced
